import { ODYSSEY_ADAPTER } from "../editor/adapter.ts";
import { DEFAULT_SURFACE, SURFACES } from "../ground/surfaces.ts";
import { ITEM_RARITIES, type Item, type ItemRarity, type Rules, type Weapon } from "../rules/types.ts";
import { LEVEL_UP_SEEN_HOOK } from "./channels.ts";
import { CHARACTER_VOICES } from "./character-voice.ts";
import { footstepHook } from "./footsteps.ts";
import { thingHook, thingHookSources, type ThingHookSource } from "./things.ts";

/**
 * The game's sound hooks (ADR-085 decision 2): every path under `assets/source/audio/` the page plays and the hook
 * played in its place when it has no file; `channels.ts` says which channel each plays on. Hooks per ability, weapon and creature template are
 * listed from the rules tables and the templates, and a thing's from its behaviour type's sound sets and verbs
 * (`things.ts`), so a new one gains its hooks with no code. A player's hurt and death are listed per voice
 * (`character-voice.ts`) and its step per surface (`ground/surfaces.ts`), so a voice or a surface is one entry there.
 */

/**
 * One hook: its path and the hook played when it has no file, or null.
 */
export interface SoundHook {
  readonly hook: string;
  readonly fallback: string | null;
}

/**
 * The stingers: the page's own death, its standing up again, a level reached, a duel won or lost (the
 * duel rows fire them), a small discovery, which a shrine is the first of (ADR-096), and a major one, kept for big
 * moments such as a region entered for the first time, which no feature plays yet (ADR-085 decision 2); and a companion
 * gained, the offer accepted and the companion named (ADR-142 decision 5, P2.26-T7; the author asked for it 2026-10-28).
 */
export const Stinger = {
  Death: "stingers/player_death",
  Respawn: "stingers/player_respawn",
  LevelUp: "stingers/player_level_up",
  DuelWon: "stingers/player_duel_won",
  DuelLost: "stingers/player_duel_lost",
  MinorDiscovery: "stingers/player_minor_discovery",
  MajorDiscovery: "stingers/player_major_discovery",
  CompanionAcquired: "stingers/companion_acquired",
} as const;

/**
 * The folder a player's hurt and death lie in: the shared sounds directly, and a folder per voice under it.
 */
const COMBAT_FOLDER = "sfx/player/combat";

/**
 * A player's shared sounds, a hit taken and a death: what a character with no voice plays, and what a voice with no
 * file of its own falls back to (ADR-085's amendment for P1.9-T83).
 */
export const PlayerSound = { Hurt: `${COMBAT_FOLDER}/hurt`, Death: `${COMBAT_FOLDER}/death` } as const;

/**
 * The chime another player's level-up plays where the page sees it (P2.4-T41, ADR-085's note of 2026-10-24): placed, on
 * Other players, one variant at random, with no fallback. The page's own level-up is the stinger.
 */
export const LevelUpSound = { Seen: LEVEL_UP_SEEN_HOOK } as const;

/**
 * The events a player's voice sounds on.
 */
export type PlayerCombatEvent = "hurt" | "death";

const PLAYER_COMBAT_EVENTS: readonly PlayerCombatEvent[] = ["hurt", "death"];

/**
 * A player's hook for a hit taken or a death, by its voice.
 *
 * @param voice The character's voice, or null for none.
 * @param event The event.
 * @returns `sfx/player/combat/<voice>/<event>`, or the shared `sfx/player/combat/<event>` for no voice.
 */
export function playerCombatHook(voice: string | null, event: PlayerCombatEvent): string {
  return voice === null ? `${COMBAT_FOLDER}/${event}` : `${COMBAT_FOLDER}/${voice}/${event}`;
}

/**
 * The interface's sounds: a press, a window opening and closing, a refusal, the chat alert, which sounds for a
 * whisper received and for a line that mentions the player (ADR-085's amendment "the chat alert"), and the maintenance
 * warning, which sounds as each maintenance toast appears (the author, 2026-10-10).
 */
export const InterfaceSound = { Click: "sfx/ui/click", Open: "sfx/ui/open", Close: "sfx/ui/close", Refusal: "sfx/ui/refusal", ChatAlert: "sfx/ui/chat/alert", Maintenance: "sfx/ui/maintenance/warning" } as const;

/**
 * The page's own purse filling: coins a creature's drop paid land in it (the author, 2026-10-10: "I have a sound for
 * when we pick up / receive money"). It plays unplaced on Player, one variant at random, and has no fallback.
 */
export const CoinSound = { Pickup: "sfx/items/coins/pickup" } as const;

/**
 * The sounds of a high tier drop landing where a page sees it (P2.8-T77, the author, 2026-10-10: "We will have a sound
 * for high tier items when they drop too."): Rare a soft chime, and Epic, Legendary and Relic each their own, rising up
 * the ladder; Common and Uncommon land silent. They lie under `sfx/world/loot/`, so they play on the World channel,
 * placed where the item lands.
 */
export const LootSound = { Rare: "sfx/world/loot/rare", Epic: "sfx/world/loot/epic", Legendary: "sfx/world/loot/legendary", Relic: "sfx/world/loot/relic" } as const;

/**
 * STAND-INS: what each loot hook plays until the author's masters for it are dropped in (the author: "Get the feature
 * ready so we can just slot the sounds in when I have them."). Each is an existing hook with a master today, picked
 * for having a file, not for fit: the chat alert for Rare, the minor discovery for Epic and the major discovery for
 * Legendary and Relic, which share it. A hook with a master of its own plays it and never reaches its stand-in, so a
 * dropped-in `sfx/world/loot/<tier>_01.wav` and a sound build replace it with no code change (OPERATIONS.md).
 */
export const LOOT_SOUND_STAND_INS: Readonly<Record<string, string>> = Object.freeze({
  [LootSound.Rare]: InterfaceSound.ChatAlert,
  [LootSound.Epic]: Stinger.MinorDiscovery,
  [LootSound.Legendary]: Stinger.MajorDiscovery,
  [LootSound.Relic]: Stinger.MajorDiscovery,
});

/**
 * The loot hook each rarity lands with, null for one that lands silent.
 */
const LOOT_HOOKS: Readonly<Record<ItemRarity, string | null>> = Object.freeze({ common: null, uncommon: null, rare: LootSound.Rare, epic: LootSound.Epic, legendary: LootSound.Legendary, relic: LootSound.Relic });

/**
 * The hook a ground item of a rarity plays as it is seen landing.
 *
 * @param rarity The item's rarity.
 * @returns The hook, or null for Common and Uncommon.
 */
export function lootHook(rarity: ItemRarity): string | null {
  return LOOT_HOOKS[rarity];
}

/**
 * A rarity's place on the ladder, from Common at 0 to Relic, so a pile landing at once sounds only its highest.
 *
 * @param rarity The rarity.
 * @returns Its rank.
 */
export function lootRank(rarity: ItemRarity): number {
  return ITEM_RARITIES.indexOf(rarity);
}

/**
 * The events an ability or a weapon sounds on: used, landing, landing critically.
 */
export type BlowEvent = "use" | "hit" | "crit";

/**
 * The events a creature sounds on.
 */
export type CreatureEvent = "notice" | "attack" | "hurt" | "death";

const BLOW_EVENTS: readonly BlowEvent[] = ["use", "hit", "crit"];
const CREATURE_EVENTS: readonly CreatureEvent[] = ["notice", "attack", "hurt", "death"];

/**
 * The event a projectile ability alone sounds on: its shot landing, where its burst plays (the author, 2026-10-10:
 * "a breaking potion sound that should play when the rat potion breaks and has the cloud appear").
 */
export type ProjectileEvent = "land";

/**
 * An ability's hook for an event.
 *
 * @param ability The ability's id in `abilities.json`.
 * @param event The event.
 * @returns The hook, `sfx/abilities/<ability>/<event>`.
 */
export function abilityHook(ability: string, event: BlowEvent | ProjectileEvent): string {
  return `sfx/abilities/${ability}/${event}`;
}

/**
 * A weapon's hook for an event.
 *
 * @param weapon The weapon's id in `weapons.json`.
 * @param event The event.
 * @returns The hook, `sfx/weapons/<weapon>/<event>`.
 */
export function weaponHook(weapon: string, event: BlowEvent): string {
  return `sfx/weapons/${weapon}/${event}`;
}

/**
 * A creature template's hook for an event.
 *
 * @param template The template's id.
 * @param event The event.
 * @returns The hook, `sfx/creatures/<template>/<event>`.
 */
export function creatureHook(template: string, event: CreatureEvent): string {
  return `sfx/creatures/${template}/${event}`;
}

/**
 * The events an NPC sounds on: greeting a player who opens a conversation, a soft blip per dialogue line, a question
 * asked, a farewell as the conversation closes, and in the world an occasional idle call and wing flutter (the
 * amendment for P2.22-T36).
 */
export type NpcEvent = "greet" | "talk" | "curious" | "farewell" | "idle" | "wing_flutter";

/**
 * The events an NPC sounds on, in the order its folder lists them.
 */
export const NPC_EVENTS: readonly NpcEvent[] = Object.freeze(["greet", "talk", "curious", "farewell", "idle", "wing_flutter"] as const);

/**
 * An NPC template's hook for an event.
 *
 * @param sound The template's `sound`, a folder under `sfx/npcs/`.
 * @param event The event.
 * @returns The hook, `sfx/npcs/<sound>/<event>`.
 * @example
 * npcHook("aethling", "greet");
 * // "sfx/npcs/aethling/greet"
 */
export function npcHook(sound: string, event: NpcEvent): string {
  return `sfx/npcs/${sound}/${event}`;
}

/**
 * The events an item sounds on: used.
 */
export type ItemEvent = "use";

const ITEM_EVENTS: readonly ItemEvent[] = ["use"];

/**
 * The sound set a watcher hears as another player's `heal` state starts (ADR-091 decision 16): the snapshot names no
 * item, so the page plays this set's `use`, as another player's attack plays the starting weapon's.
 */
export const HEAL_SOUND_SET = "potion";

/**
 * An item's hook for an event.
 *
 * @param set The item's sound set, a folder under `sfx/items/` (an item's `sound` in `items.json`).
 * @param event The event.
 * @returns The hook, `sfx/items/<set>/<event>`.
 * @example
 * itemHook("potion", "use");
 * // "sfx/items/potion/use"
 */
export function itemHook(set: string, event: ItemEvent): string {
  return `sfx/items/${set}/${event}`;
}

/**
 * The sound sets a table of items names, each once in the table's order; an item with no sound names none.
 *
 * @param items The items, as far as their sound.
 * @returns The sets.
 * @example
 * itemSoundSets([{ sound: "potion" }, { sound: null }]);
 * // ["potion"]
 */
export function itemSoundSets(items: readonly Pick<Item, "sound">[]): string[] {
  const sets: string[] = [];
  for (const item of items) {
    if (item.sound !== null && !sets.includes(item.sound)) {
      sets.push(item.sound);
    }
  }
  return sets;
}

/**
 * A weapon as the hooks read it: its id and the abilities its blows name.
 */
export type HookWeapon = Pick<Weapon, "id"> & { readonly blows: readonly { readonly ability: string }[] };

/**
 * What the hooks are listed from: the abilities' ids, those of them whose shape is a projectile, none when absent, the
 * weapons with the abilities their blows name, the creature templates' ids, the things' sound sets with their events,
 * none when absent, the items' sound sets, none when absent, and the NPC templates' sound folders, none when absent.
 */
export interface SoundHookSources {
  readonly abilities: readonly string[];
  readonly projectiles?: readonly string[];
  readonly weapons: readonly HookWeapon[];
  readonly templates: readonly string[];
  readonly things?: readonly ThingHookSource[];
  readonly items?: readonly string[];
  readonly npcSounds?: readonly string[];
}

/**
 * The weapon whose blows name an ability, the first in table order.
 *
 * @param weapons The weapons.
 * @param ability The ability's id.
 * @returns The weapon's id, or null when no weapon names it.
 */
export function weaponOfAbility(weapons: SoundHookSources["weapons"], ability: string): string | null {
  return weapons.find((weapon) => weapon.blows.some((blow) => blow.ability === ability))?.id ?? null;
}

/**
 * The fallback of a blow event's hook: a crit falls back to the same owner's hit, and an ability's use or hit to its
 * weapon's.
 *
 * @param event The event.
 * @param own The hook of the same owner for an event.
 * @param weapon The weapon's hook for an event, or null for none.
 * @returns The fallback hook, or null.
 */
function blowFallback(event: BlowEvent, own: (event: BlowEvent) => string, weapon: ((event: BlowEvent) => string) | null): string | null {
  if (event === "crit") {
    return own("hit");
  }
  return weapon === null ? null : weapon(event);
}

/**
 * Every hook the page plays, sorted by path.
 *
 * @param sources The abilities, weapons, creature templates, things, items and NPC sound folders.
 * @returns The hooks, each once.
 */
export function soundHooks(sources: SoundHookSources): SoundHook[] {
  const hooks = new Map<string, SoundHook>();
  const add = (hook: string, fallback: string | null = null): void => {
    hooks.set(hook, { hook, fallback });
  };
  for (const stinger of Object.values(Stinger)) {
    add(stinger);
  }
  for (const event of PLAYER_COMBAT_EVENTS) {
    add(playerCombatHook(null, event));
    for (const voice of CHARACTER_VOICES) {
      add(playerCombatHook(voice.id, event), playerCombatHook(null, event));
    }
  }
  for (const surface of SURFACES) {
    add(footstepHook(surface.id), surface.id === DEFAULT_SURFACE ? null : footstepHook(DEFAULT_SURFACE));
  }
  for (const sound of Object.values(InterfaceSound)) {
    add(sound);
  }
  for (const sound of Object.values(LootSound)) {
    add(sound, LOOT_SOUND_STAND_INS[sound] ?? null);
  }
  add(CoinSound.Pickup);
  add(LevelUpSound.Seen);
  for (const ability of sources.abilities) {
    const weapon = weaponOfAbility(sources.weapons, ability);
    for (const event of BLOW_EVENTS) {
      add(abilityHook(ability, event), blowFallback(event, (other) => abilityHook(ability, other), weapon === null ? null : (other) => weaponHook(weapon, other)));
    }
  }
  for (const ability of sources.projectiles ?? []) {
    add(abilityHook(ability, "land"));
  }
  for (const weapon of sources.weapons) {
    for (const event of BLOW_EVENTS) {
      add(weaponHook(weapon.id, event), blowFallback(event, (other) => weaponHook(weapon.id, other), null));
    }
  }
  for (const template of sources.templates) {
    for (const event of CREATURE_EVENTS) {
      add(creatureHook(template, event));
    }
  }
  for (const thing of sources.things ?? []) {
    for (const event of thing.events) {
      add(thingHook(thing.set, event), thing.fallback === null ? null : thingHook(thing.fallback, event));
    }
  }
  for (const set of sources.items ?? []) {
    for (const event of ITEM_EVENTS) {
      add(itemHook(set, event));
    }
  }
  for (const sound of sources.npcSounds ?? []) {
    for (const event of NPC_EVENTS) {
      add(npcHook(sound, event));
    }
  }
  return [...hooks.values()].sort((a, b) => (a.hook < b.hook ? -1 : a.hook > b.hook ? 1 : 0));
}

/**
 * Every hook the page plays for a set of rules and the creature templates, with the hooks of the things the game
 * registers and of the sound sets the rules' items name.
 *
 * @param rules The rules, whose tables name the abilities, the weapons and the items.
 * @param templates The creature templates' ids.
 * @param npcSounds The sound folders the NPC templates name, none by default.
 * @returns The hooks, each once, sorted by path.
 */
export function soundHooksOfRules(rules: Pick<Rules, "tables">, templates: readonly string[], npcSounds: readonly string[] = []): SoundHook[] {
  return soundHooks({ npcSounds, abilities: rules.tables.abilities.map((ability) => ability.id), projectiles: rules.tables.abilities.filter((ability) => ability.shape.kind === "projectile").map((ability) => ability.id), weapons: rules.tables.weapons, templates, things: thingHookSources(ODYSSEY_ADAPTER.objectTypes ?? []), items: itemSoundSets(rules.tables.items) });
}
